import React, {FC, useMemo, useRef, useState} from "react";
import {Button, Col, Container, InputGroup, Row, Table} from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar, faFileExcel, faPrint} from "@fortawesome/free-solid-svg-icons";
import {collection, query, where} from "firebase/firestore";
import {useCollectionData} from "react-firebase-hooks/firestore";
import ExcelJS from "exceljs";
import {appFirestore} from "../../../../../index";
import {dateToString, dateWithMaxTime, dateWithZeroTime, timeToString} from "../../../../tools";
import {MaybeDate} from "../../../../types/Maybe";
import {RRLogisticType} from "../../RRLogisticType";
import {logisticsFBConverter} from "../../LogisticsFBConverter";
import {RREventType} from "../../../../types/RREventType";
import {selectAllEvents} from "../../../../../features/AppDataSlice";
import {useAppSelector} from "../../../../hooks";

type ScheduleRow = {
    id: string;
    date: Date;
    time: string;
    location: string;
    driver: string;
    vehicle: string;
    activity: string;
    client: string;
    contact: string;
    eventNumber: string;
};

function dayTypeToText(dayType: RRLogisticType["dayType"]): string {
    if (dayType === "HOLIDAY") {
        return "ОТПУСК";
    }
    if (dayType === "SICK") {
        return "БОЛНИЧЕН";
    }
    if (dayType === "DAYOFF") {
        return "ОТСЪСТВИЕ";
    }
    return "РАБОТА";
}

function resolveActivity(logistic: RRLogisticType): string {
    if (logistic.dayType !== "WORKDAY") {
        return dayTypeToText(logistic.dayType);
    }

    if (logistic.reportNotes.trim().length > 0) {
        return logistic.reportNotes.trim();
    }

    if (logistic.planNotes.trim().length > 0) {
        return logistic.planNotes.trim();
    }

    if (logistic.workInStore) {
        return "СКЛАД";
    }

    return "ТРАНСПОРТ";
}

function resolveLocation(logistic: RRLogisticType, eventByNumber: Map<string, RREventType>): string {
    const from = logistic.startPoint.trim();
    const to = logistic.endPoint.trim();
    if (from.length > 0 && to.length > 0) {
        return `${from} -> ${to}`;
    }
    if (from.length > 0) {
        return from;
    }
    if (to.length > 0) {
        return to;
    }

    const event = eventByNumber.get(logistic.eventNumber.trim());
    return event?.place?.trim() ?? "";
}

function resolveVehicle(logistic: RRLogisticType): string {
    const name = logistic.truck.name.trim();
    const plate = logistic.truck.plate.trim();

    if (name.length > 0 && plate.length > 0) {
        return `${name} (${plate})`;
    }

    return name || plate;
}

const LogisticsScheduleReportView: FC = () => {
    const today = dateWithZeroTime(new Date()) ?? new Date();
    const [periodFrom, setPeriodFrom] = useState<MaybeDate>(today);
    const [periodTo, setPeriodTo] = useState<MaybeDate>(today);
    const [queryFrom, setQueryFrom] = useState<Date>(today);
    const [queryTo, setQueryTo] = useState<Date>(dateWithMaxTime(today) ?? today);
    const tableRef = useRef<HTMLTableElement>(null);
    const allEvents = useAppSelector(selectAllEvents);

    const logisticsQuery = useMemo(() => query(
        collection(appFirestore, "logistics"),
        where("startDate", ">=", queryFrom),
        where("startDate", "<=", queryTo)
    ).withConverter(logisticsFBConverter), [queryFrom, queryTo]);

    const [logistics, loadingLogistics, errorLogistics] = useCollectionData(logisticsQuery);

    const eventByNumber = useMemo(() => {
        const map = new Map<string, RREventType>();
        allEvents.data.forEach(event => {
            if (event.eventNumber && event.eventNumber.trim().length > 0) {
                map.set(event.eventNumber.trim(), event);
            }
        });
        return map;
    }, [allEvents.data]);

    const rows = useMemo(() => {
        const source = logistics ?? [];
        const sorted = [...source].sort((left, right) => {
            const leftDay = dateWithZeroTime(left.startDate)?.getTime() ?? left.startDate.getTime();
            const rightDay = dateWithZeroTime(right.startDate)?.getTime() ?? right.startDate.getTime();
            const dayDiff = leftDay - rightDay;
            if (dayDiff !== 0) {
                return dayDiff;
            }

            const leftIsHoliday = left.dayType === "HOLIDAY";
            const rightIsHoliday = right.dayType === "HOLIDAY";
            if (leftIsHoliday !== rightIsHoliday) {
                return leftIsHoliday ? 1 : -1;
            }

            return left.startDate.getTime() - right.startDate.getTime();
        });

        return sorted.map((logistic, index): ScheduleRow => {
            const eventNumber = logistic.eventNumber.trim();
            const event = eventByNumber.get(eventNumber);
            const timeValue = timeToString(logistic.startDate);

            return {
                id: logistic.id ?? `${logistic.driverId}_${logistic.startDate.getTime()}_${index}`,
                date: logistic.startDate,
                time: timeValue === "00:00" ? "" : timeValue,
                location: resolveLocation(logistic, eventByNumber),
                driver: logistic.driverName,
                vehicle: resolveVehicle(logistic),
                activity: resolveActivity(logistic),
                client: event?.clientName?.trim() ?? "",
                contact: event?.clientSpoc?.trim() ?? "",
                eventNumber,
            };
        }).filter(row => row.activity.trim().toUpperCase() !== "СКЛАД");
    }, [eventByNumber, logistics]);

    function applyFilters() {
        const from = dateWithZeroTime(periodFrom ?? today) ?? today;
        const to = dateWithMaxTime(periodTo ?? periodFrom ?? today) ?? from;
        setQueryFrom(from);
        setQueryTo(to);
    }

    async function exportExcel() {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("График");

        worksheet.columns = [
            {header: "Дата:", key: "date", width: 14},
            {header: "Час:", key: "time", width: 10},
            {header: "Курс:", key: "course", width: 42},
            {header: "Шофьор:", key: "driver", width: 28},
            {header: "МПС:", key: "vehicle", width: 28},
            {header: "Дейност:", key: "activity", width: 24},
            {header: "Клиент:", key: "client", width: 20},
            {header: "Контакт:", key: "contact", width: 20},
            {header: "№ събитие:", key: "eventNumber", width: 14},
        ];

        const headerRow = worksheet.getRow(1);
        headerRow.eachCell(cell => {
            cell.font = {bold: true, color: {argb: "FF000000"}};
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: {argb: "FFB4B600"}
            };
        });

        rows.forEach((row, index) => {
            const currentDate = dateToString(row.date);
            const previousDate = index > 0 ? dateToString(rows[index - 1].date) : "";
            const shouldAddSeparator = index > 0 && previousDate !== currentDate;

            if (shouldAddSeparator) {
                const separatorRow = worksheet.addRow(["", "", "", "", "", "", "", "", ""]);
                separatorRow.height = 10;
                separatorRow.eachCell(cell => {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: {argb: "FFB4B600"}
                    };
                });
            }

            const excelRow = worksheet.addRow({
                date: currentDate,
                time: row.time,
                course: row.location,
                driver: row.driver,
                vehicle: row.vehicle,
                activity: row.activity,
                client: row.client,
                contact: row.contact,
                eventNumber: row.eventNumber
            });

            if (row.activity.trim().toUpperCase() === "ОТПУСК") {
                excelRow.eachCell(cell => {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: {argb: "FFF8D7DA"}
                    };
                    cell.font = {color: {argb: "FF842029"}};
                });
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob(
            [buffer],
            {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "График.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    function printReport() {
        window.print();
    }

    return (
        <Container fluid>
            <h1>Справки и отчети</h1>
            <h3>График</h3>

            <Row className={"g-2 mt-3 flex-nowrap d-print-none"}>
                <Col xs={"auto"}>
                    <Row>
                        <Col xs={"auto"} className={"lh-2-5em"}>Начална дата:</Col>
                        <Col xs={"auto"}>
                            <InputGroup className="mb-2 w-180px flex-nowrap">
                                <ReactDatePicker
                                    id={"schedulePeriodFrom"}
                                    className={"form-control flex-nowrap"}
                                    selected={periodFrom}
                                    onChange={date => setPeriodFrom(date ?? today)}
                                    value={dateToString(periodFrom)}
                                    maxDate={periodTo ?? undefined}
                                />
                                <label htmlFor={"schedulePeriodFrom"}>
                                    <InputGroup.Text className={"lh-2-5em"}>
                                        <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                    </InputGroup.Text>
                                </label>
                            </InputGroup>
                        </Col>
                    </Row>
                </Col>
                <Col xs={"auto"}>
                    <Row>
                        <Col xs={"auto"} className={"lh-2-5em"}>Крайна дата:</Col>
                        <Col xs={"auto"}>
                            <InputGroup className="mb-2 w-180px flex-nowrap">
                                <ReactDatePicker
                                    id={"schedulePeriodTo"}
                                    className={"form-control flex-nowrap"}
                                    selected={periodTo}
                                    onChange={date => setPeriodTo(date ?? periodFrom ?? today)}
                                    value={dateToString(periodTo)}
                                    minDate={periodFrom ?? undefined}
                                />
                                <label htmlFor={"schedulePeriodTo"}>
                                    <InputGroup.Text className={"lh-2-5em"}>
                                        <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                    </InputGroup.Text>
                                </label>
                            </InputGroup>
                        </Col>
                    </Row>
                </Col>
                <Col>
                    <Button onClick={applyFilters}>Изчисли</Button>&nbsp;
                    <Button onClick={printReport} variant={"outline-secondary"}>
                        <FontAwesomeIcon icon={faPrint}/>
                    </Button>&nbsp;
                    <Button onClick={exportExcel} variant={"outline-success"}>
                        <FontAwesomeIcon icon={faFileExcel}/>
                    </Button>
                </Col>
            </Row>

            {
                errorLogistics &&
                <Row className={"g-2 mt-3"}>
                    <Col className={"text-danger fw-bold"}>
                        Грешка при зареждане: {errorLogistics.message}
                    </Col>
                </Row>
            }

            {
                loadingLogistics &&
                <Row className={"g-2 mt-3"}>
                    <Col className={"fw-bold"}>Зареждане...</Col>
                </Row>
            }

            {
                !loadingLogistics && !errorLogistics && rows.length === 0 &&
                <Row className={"g-2 mt-3"}>
                    <Col className={"fw-bold"}>Няма намерени данни за избрания период.</Col>
                </Row>
            }

            {
                !loadingLogistics && !errorLogistics && rows.length > 0 &&
                <Row className={"g-2 mt-3 bg-white"}>
                    <Col xs={"12"}>
                        <Table ref={tableRef} bordered hover size={"sm"}>
                            <thead>
                            <tr style={{backgroundColor: "#b4b600"}}>
                                <th>Дата:</th>
                                <th>Час:</th>
                                <th>Курс:</th>
                                <th>Шофьор:</th>
                                <th>МПС:</th>
                                <th>Дейност:</th>
                                <th>Клиент:</th>
                                <th>Контакт:</th>
                                <th>№ събитие:</th>
                            </tr>
                            </thead>
                            <tbody>
                            {
                                rows.map((row, index) => {
                                    const currentDate = dateToString(row.date);
                                    const previousDate = index > 0 ? dateToString(rows[index - 1].date) : "";
                                    const shouldRenderDateSeparator = index > 0 && previousDate !== currentDate;

                                    return (
                                        <React.Fragment key={row.id}>
                                            {
                                                shouldRenderDateSeparator &&
                                                <tr>
                                                    <td colSpan={9} style={{backgroundColor: "#b4b600", height: "14px", padding: 0}}/>
                                                </tr>
                                            }
                                            <tr
                                                style={row.activity.trim().toUpperCase() === "ОТПУСК"
                                                    ? {backgroundColor: "#f8d7da", color: "#842029"}
                                                    : undefined}
                                            >
                                                <td>{currentDate}</td>
                                                <td>{row.time}</td>
                                                <td>{row.location}</td>
                                                <td>{row.driver}</td>
                                                <td>{row.vehicle}</td>
                                                <td>{row.activity}</td>
                                                <td>{row.client}</td>
                                                <td>{row.contact}</td>
                                                <td>{row.eventNumber}</td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })
                            }
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            }
        </Container>
    );
};

export default LogisticsScheduleReportView;
